// Service layer for the Image Browser / Uploader / Card Editor features.
// Each function maps 1:1 to a backend endpoint. axios's baseURL is set globally
// in main.js, so paths here are relative.

import axios from 'axios';

/**
 * List all images. v1: backend returns the full library (no server-side
 * filtering); the caller filters client-side. Filters arg is currently unused
 * but kept on the signature so the eventual server-filter switch is one line.
 *
 * @param {{ category?: number|string, status?: string, search?: string }} [filters]
 * @returns {Promise<import('./imageHelpers').ImageRecord[]>}
 */
// eslint-disable-next-line no-unused-vars
export async function fetchImages(filters = {}) {
  // TODO: when server-side filtering lands, pass filters as query params.
  const { data } = await axios.get('/images');
  return data.images;
}

// Memoized full-library load as a Map<id, ImageRecord>, for read-only
// consumers (the quiz runtime content card) that need to resolve
// image_id → record without each card refetching. There is no GET
// /images/:id endpoint yet, so we load the library once and cache it for
// the session. A failed load is not cached, so the next caller retries.
//
// Pass forceRefresh=true to discard the cached map and refetch — the
// editors do this on mount so an image uploaded mid-session (already
// visible in the Image Browser, which fetches fresh) shows up in the
// picker and in the live preview too. Replacing the shared promise means
// any card that (re)mounts afterwards resolves against the fresh library.
let _imageMapPromise = null;
export function loadImageMap(forceRefresh = false) {
  if (forceRefresh) _imageMapPromise = null;
  if (!_imageMapPromise) {
    _imageMapPromise = fetchImages()
      .then((list) => {
        const map = new Map();
        for (const img of list) map.set(img.id, img);
        return map;
      })
      .catch((e) => {
        _imageMapPromise = null;
        throw e;
      });
  }
  return _imageMapPromise;
}

/**
 * Fetch a single image by id, including all per-language label / alt-text fields.
 * @param {number} id
 */
// eslint-disable-next-line no-unused-vars
export async function fetchImage(id) {
  // TODO: GET /images/:id (backend not implemented yet)
  throw new Error('fetchImage: not implemented');
}

/**
 * Upload a new image. Sends multipart/form-data to POST /images/upload.
 * Returns the inserted record (same shape as a row from fetchImages()).
 *
 * IMPORTANT: do NOT manually set Content-Type. axios sets the multipart
 * boundary automatically when the body is a FormData instance.
 *
 * @param {File} file
 * @param {Object} metadata
 * @param {number} metadata.category_id
 * @param {number} metadata.license_id
 * @param {number} metadata.image_type_id
 * @param {string} [metadata.label_en]
 * @param {string} [metadata.label_de]
 * @param {string} [metadata.label_fr]
 * @param {string} [metadata.label_it]
 * @param {string} [metadata.alt_text_en]
 * @param {string} [metadata.alt_text_de]
 * @param {string} [metadata.alt_text_fr]
 * @param {string} [metadata.alt_text_it]
 * @param {string} [metadata.curator_notes]
 * @returns {Promise<import('./imageHelpers').ImageRecord>}
 */
export async function uploadImage(file, metadata) {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('category_id', String(metadata.category_id));
  fd.append('license_id', String(metadata.license_id));
  fd.append('image_type_id', String(metadata.image_type_id));
  fd.append('label_en', metadata.label_en || '');
  fd.append('label_de', metadata.label_de || '');
  fd.append('label_fr', metadata.label_fr || '');
  fd.append('label_it', metadata.label_it || '');
  fd.append('alt_text_en', metadata.alt_text_en || '');
  fd.append('alt_text_de', metadata.alt_text_de || '');
  fd.append('alt_text_fr', metadata.alt_text_fr || '');
  fd.append('alt_text_it', metadata.alt_text_it || '');
  fd.append('curator_notes', metadata.curator_notes || '');

  const { data } = await axios.post('/images/upload', fd);
  return data;
}

/**
 * Update an image's metadata or status.
 */
// eslint-disable-next-line no-unused-vars
export async function updateImage(id, fields) {
  // TODO: PATCH /images/:id (backend not implemented yet)
  throw new Error('updateImage: not implemented');
}

/**
 * Delete an image. Backend may soft-delete (set status=ARCHIVED) instead.
 */
// eslint-disable-next-line no-unused-vars
export async function deleteImage(id) {
  // TODO: DELETE /images/:id (backend not implemented yet)
  throw new Error('deleteImage: not implemented');
}
